import {
  closePath,
  degrees,
  beginText,
  endText,
  fill,
  lineTo,
  moveTo,
  PDFArray,
  PDFDocument,
  PDFHexString,
  PDFName,
  PDFNumber,
  PDFOperator,
  popGraphicsState,
  pushGraphicsState,
  rotateAndSkewTextRadiansAndTranslate,
  rgb,
  setFillingColor,
  setFontAndSize,
  StandardFonts,
} from 'pdf-lib'
import fontkit from '@pdf-lib/fontkit'
import { fontStack, hexToRgb } from './utils.js'

function paintWhitePageBackground(doc, page, width, height) {
  page.node.normalize()
  const contents = page.node.Contents()
  if (!contents?.insert) return

  const backgroundStream = doc.context.contentStream([
    pushGraphicsState(),
    setFillingColor(rgb(1, 1, 1)),
    moveTo(0, 0),
    lineTo(0, height),
    lineTo(width, height),
    lineTo(width, 0),
    closePath(),
    fill(),
    popGraphicsState(),
  ])
  contents.insert(0, doc.context.register(backgroundStream))
}

function annotationPlacement(annotation, width, height, rotation) {
  const normalizedRotation = ((rotation % 360) + 360) % 360
  const rotated = normalizedRotation === 90 || normalizedRotation === 270
  const displayWidth = rotated ? height : width
  const displayHeight = rotated ? width : height
  const screenX = annotation.x * displayWidth
  const screenBaselineY = annotation.y * displayHeight + annotation.size / 2

  if (normalizedRotation === 90) {
    return { x: screenBaselineY, y: screenX, rotate: degrees(90) }
  }
  if (normalizedRotation === 180) {
    return { x: width - screenX, y: screenBaselineY, rotate: degrees(180) }
  }
  if (normalizedRotation === 270) {
    return { x: width - screenBaselineY, y: height - screenX, rotate: degrees(270) }
  }
  return { x: screenX, y: height - screenBaselineY, rotate: degrees(0) }
}

// Map every font on the page by its BaseFont name to its resource key. This is
// how an edited text run is linked back to the exact font it was drawn with —
// it works for both composite (Type0/CID) and simple (Type1/TrueType) fonts,
// unlike a glyph-width fingerprint which only lines up for CID fonts.
function sourceFontKeys(doc, page) {
  const map = new Map()
  const resources = page.node.Resources()
  const fonts = resources && doc.context.lookup(resources.get(PDFName.of('Font')))
  if (!fonts?.keys) return map

  for (const key of fonts.keys()) {
    const font = doc.context.lookup(fonts.get(key))
    const baseFont = font?.get?.(PDFName.of('BaseFont'))?.decodeText?.() || ''
    if (baseFont && !map.has(baseFont)) map.set(baseFont, key.decodeText())
  }
  return map
}

// Draw a line using the page's own font resource. Characters are encoded with
// the original glyph codes (2 bytes for composite fonts, 1 byte for simple
// ones). A space with no glyph in the font — common in LaTeX text fonts — is
// emitted as a TJ position adjustment so following words keep their spacing.
// Returns false when a non-space character can't be represented so the caller
// can fall back to a standard font for the whole line.
function drawOriginalText(page, line, options) {
  const bytesPerCode = options.composite ? 2 : 1
  const parts = []
  let run = ''
  const flushRun = () => {
    if (run) {
      parts.push(PDFHexString.of(run))
      run = ''
    }
  }

  for (const char of line) {
    const code = options.charToCid[char]
    if (Number.isInteger(code)) {
      run += code.toString(16).padStart(bytesPerCode * 2, '0')
    } else if (char === ' ') {
      flushRun()
      parts.push(PDFNumber.of(-(options.spaceWidthEm || 0.3) * 1000))
    } else {
      return false
    }
  }
  flushRun()
  if (!parts.length) return true

  const array = PDFArray.withContext(options.context)
  parts.forEach(part => array.push(part))
  page.pushOperators(
    pushGraphicsState(),
    beginText(),
    setFillingColor(options.color),
    setFontAndSize(PDFName.of(options.resourceKey), options.size),
    rotateAndSkewTextRadiansAndTranslate(0, 0, 0, options.x, options.y),
    PDFOperator.of('TJ' as any, [array]),
    endText(),
    popGraphicsState(),
  )
  return true
}

export const saveMethods = {
  async save() {
    if (!this.documents.size || !this.visiblePages.length) return
    if (this.sourcePasswordProtected) {
      await this.saveProtectedPdfAsFlattenedCopy()
      return
    }
    const outDoc = await PDFDocument.create()
    outDoc.registerFontkit(fontkit)
    const font = await outDoc.embedFont(StandardFonts.Helvetica)
    const boldFont = await outDoc.embedFont(StandardFonts.HelveticaBold)
    const symbolFont = await outDoc.embedFont(StandardFonts.ZapfDingbats)
    // Standard fonts only. We deliberately never re-embed the source subset
    // font: PDF-embedded CID subsets have no `cmap` table, and fontkit throws
    // ("cmap: missing required table") the moment it measures or lays out text
    // with them. Original glyphs are reproduced by drawOriginalText,
    // which references the copied page's own font resource directly; this
    // fallback is only for text that can't be drawn with the original font.
    const fallbackFontFor = (text, weight = 400) => {
      const preferred = weight >= 600 ? boldFont : font
      try {
        preferred.widthOfTextAtSize(text, 1)
        return preferred
      } catch {
        return symbolFont
      }
    }

    // Copy every kept page in ONE call so pdf-lib de-duplicates shared
    // resources (fonts especially). Copying page-by-page duplicates each
    // embedded font once per page and bloats the file several times over.
    const keptPages = this.visiblePages
    const sourceDocs = new Map()
    const copiedPages = new Map()
    for (const [documentId, source] of this.documents) {
      const pagesFromSource = keptPages.filter(page => page.documentId === documentId)
      if (!pagesFromSource.length) continue
      const srcDoc = await PDFDocument.load(source.sourceBytes.slice(0))
      sourceDocs.set(documentId, srcDoc)
      const copies = await outDoc.copyPages(srcDoc, pagesFromSource.map(page => page.sourceIndex))
      pagesFromSource.forEach((page, index) => copiedPages.set(page.id, copies[index]))
    }

    for (let pageOrder = 0; pageOrder < keptPages.length; pageOrder++) {
      const pageState = keptPages[pageOrder]
      const srcDoc = sourceDocs.get(pageState.documentId)
      const copiedPage = copiedPages.get(pageState.id)
      const sourcePage = srcDoc.getPage(pageState.sourceIndex)
      const fontKeys = sourceFontKeys(srcDoc, sourcePage)
      const baseRotation = copiedPage.getRotation().angle || 0
      const rotation = (baseRotation + pageState.rotation + 360) % 360
      copiedPage.setRotation(degrees(rotation))
      const { width, height } = copiedPage.getSize()
      paintWhitePageBackground(outDoc, copiedPage, width, height)

      for (const edit of Object.values(pageState.textEdits) as any[]) {
        const x = edit.x * width
        const boxHeight = edit.height * height
        const y = height - (edit.y * height) - boxHeight
        const coverX = (edit.coverX ?? edit.x) * width
        const coverHeight = (edit.coverHeight ?? edit.height) * height
        const coverY = height - ((edit.coverY ?? edit.y) * height) - coverHeight
        copiedPage.drawRectangle({
          x: Math.max(0, coverX),
          y: Math.max(0, coverY),
          width: Math.min(width - coverX, (edit.coverWidth ?? edit.width) * width),
          height: coverHeight,
          color: hexToRgb(edit.coverColor || this.sampleTextEditCoverColor(edit) || '#ffffff'),
        })
        const fontSize = Math.max(8, (edit.fontHeight || edit.height) * height)
        const lineHeight = fontSize * 1.2
        const resourceKey = fontKeys.get(edit.fontName)
        const editFont = fallbackFontFor(edit.text, edit.fontWeight)
        // Measure with the canvas using the same font family as the on-screen
        // preview, so exported line breaks match what the user saw.
        this.ctx.save()
        this.ctx.font = `${edit.fontWeight || 400} ${fontSize}px ${fontStack(edit.fontFamily)}`
        const lines = this.wrapText(edit.text, edit.width * width, text => this.ctx.measureText(text).width)
        this.ctx.restore()
        lines.forEach((line, index) => {
          const lineY = y + boxHeight - fontSize * 1.14 - index * lineHeight
          if (lineY >= y - lineHeight * 0.25) {
            const color = hexToRgb(edit.textColor || '#111820')
            const drewWithOriginalFont = resourceKey && drawOriginalText(copiedPage, line, {
              x,
              y: lineY,
              size: fontSize,
              color,
              resourceKey,
              charToCid: edit.charToCid,
              composite: edit.composite,
              spaceWidthEm: edit.spaceWidthEm,
              context: outDoc.context,
            })
            if (!drewWithOriginalFont) {
              copiedPage.drawText(line, {
                x,
                y: lineY,
                size: fontSize,
                font: editFont,
                color,
              })
            }
          }
        })
      }
      for (const annotation of pageState.annotations) {
        const annotationFont = fallbackFontFor(annotation.text)
        const placement = annotationPlacement(annotation, width, height, rotation)
        copiedPage.drawText(annotation.text, {
          x: placement.x,
          y: placement.y,
          rotate: placement.rotate,
          size: annotation.size,
          font: annotationFont,
          color: hexToRgb(annotation.color),
        })
      }
      await this.drawVisualAnnotationsToPdf(outDoc, copiedPage, pageState, rotation)
      outDoc.addPage(copiedPage)
    }

    const bytes = await outDoc.save()
    const blob = new Blob([bytes as Uint8Array<ArrayBuffer>], { type: 'application/pdf' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = this.fileName.replace(/\.pdf$/i, '') + '-edited.pdf'
    link.click()
    URL.revokeObjectURL(url)
    this.savedState = this.serializeState()
    this.updateDirtyState()
    this.setStatus('Edited PDF saved.')
  },

  async saveProtectedPdfAsFlattenedCopy() {
    this.setStatus('Preparing an unprotected flattened copy…')
    const outDoc = await PDFDocument.create()
    const scale = 2

    for (const pageState of this.visiblePages) {
      const pdfPage = await this.pdfForPage(pageState).getPage(pageState.sourceIndex + 1)
      const viewport = pdfPage.getViewport({
        scale,
        rotation: pdfPage.rotate + pageState.rotation,
      })
      const canvas = document.createElement('canvas')
      canvas.width = Math.ceil(viewport.width)
      canvas.height = Math.ceil(viewport.height)
      const ctx = canvas.getContext('2d')
      await pdfPage.render({ canvasContext: ctx, viewport }).promise

      ctx.save()
      ctx.textBaseline = 'alphabetic'
      for (const edit of Object.values(pageState.textEdits) as any[]) {
        const x = edit.x * viewport.width
        const y = edit.y * viewport.height
        const boxWidth = edit.width * viewport.width
        const boxHeight = edit.height * viewport.height
        const coverX = (edit.coverX ?? edit.x) * viewport.width
        const coverY = (edit.coverY ?? edit.y) * viewport.height
        const coverWidth = (edit.coverWidth ?? edit.width) * viewport.width
        const coverHeight = (edit.coverHeight ?? edit.height) * viewport.height
        const fontSize = Math.max(8 * scale, (edit.fontHeight || edit.height) * viewport.height)
        ctx.fillStyle = edit.coverColor || '#ffffff'
        ctx.fillRect(coverX, coverY, coverWidth, coverHeight)
        ctx.fillStyle = edit.textColor || '#111820'
        ctx.font = `${edit.fontWeight || 400} ${fontSize}px ${fontStack(edit.fontFamily)}`
        const lineHeight = fontSize * 1.2
        const lines = this.wrapText(edit.text, boxWidth, text => ctx.measureText(text).width)
        lines.forEach((line, index) => {
          const baseline = y + fontSize + index * lineHeight
          if (baseline <= y + boxHeight + lineHeight * 0.25) ctx.fillText(line, x, baseline)
        })
      }
      for (const annotation of pageState.annotations) {
        ctx.fillStyle = annotation.color || '#333333'
        ctx.font = `${annotation.size * scale}px ${fontStack(annotation.fontFamily)}`
        ctx.textBaseline = 'middle'
        ctx.fillText(annotation.text, annotation.x * viewport.width, annotation.y * viewport.height)
      }
      await this.drawVisualAnnotationsToCanvas(ctx, pageState, viewport.width, viewport.height, scale)
      ctx.restore()

      const png = await outDoc.embedPng(canvas.toDataURL('image/png'))
      const outputWidth = viewport.width / scale
      const outputHeight = viewport.height / scale
      const outputPage = outDoc.addPage([outputWidth, outputHeight])
      outputPage.drawImage(png, { x: 0, y: 0, width: outputWidth, height: outputHeight })
    }

    const bytes = await outDoc.save()
    this.downloadPdfBytes(bytes)
    this.savedState = this.serializeState()
    this.updateDirtyState()
    this.setStatus('Unprotected flattened PDF saved. Text in the downloaded copy is no longer selectable.')
  },

  downloadPdfBytes(bytes) {
    const blob = new Blob([bytes as Uint8Array<ArrayBuffer>], { type: 'application/pdf' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = this.fileName.replace(/\.pdf$/i, '') + '-edited.pdf'
    link.click()
    setTimeout(() => URL.revokeObjectURL(url), 0)
  },
}
